import { Helmet } from 'react-helmet-async';

interface Product {
  id: string;
  title: string;
  description: string;
  price: number;
  images?: string[];
  currency: string;
}

interface SellerInfo {
  business_name: string;
}

interface ProductMetaTagsProps {
  product: Product;
  seller?: SellerInfo | null;
}

const APP_URL = "https://sabitradeng.com";
// Direct Supabase URL for OG images - bypasses unreliable Vercel rewrites
const OG_IMAGE_URL = "https://fdcqcibrdsuvmdwagulk.supabase.co/functions/v1/og-product-image";

export function ProductMetaTags({ product, seller }: ProductMetaTagsProps) {
  if (!product) return null;

  const excerpt = product.description?.length > 160 
    ? product.description.substring(0, 157) + '...' 
    : product.description || '';

  const title = `${product.title} - ₦${product.price.toLocaleString()} | ${seller?.business_name || 'SabiTradeNG'}`;
  const description = `${excerpt} Buy now for ₦${product.price.toLocaleString()} on SabiTradeNG.`;
  
  // Use direct Supabase URL for og-product-image edge function for 1200x630 OG image
  const ogImage = `${OG_IMAGE_URL}?productId=${product.id}&width=1200&height=630`;
  const productUrl = `${APP_URL}/products/${product.id}`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={productUrl} />

      {/* Open Graph Tags */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={productUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:type" content="image/jpeg" />
      <meta property="og:site_name" content="SabiTradeNG" />

      {/* Product Specific Tags */}
      <meta property="product:price:amount" content={product.price.toString()} />
      <meta property="product:price:currency" content={product.currency || 'NGN'} />

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:width" content="1200" />
      <meta name="twitter:image:height" content="630" />
      <meta name="twitter:image:type" content="image/jpeg" />
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Product",
        name: product.title,
        description: product.description || undefined,
        image: product.images?.length ? product.images : undefined,
        brand: seller?.business_name ? { "@type": "Brand", name: seller.business_name } : undefined,
        offers: {
          "@type": "Offer",
          url: productUrl,
          price: product.price,
          priceCurrency: product.currency || "NGN",
          availability: "https://schema.org/InStock",
        },
      })}</script>
    </Helmet>
  );
}
